import React, { useState, useEffect } from 'react';
import { View, StyleSheet, ScrollView } from 'react-native';
import { Text, useTheme } from 'react-native-paper';
import { useRoute } from '@react-navigation/native';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { TreeView, TreeNode } from '../components/TreeView';
import { AppHeader } from '../components/AppHeader';
import { ResizablePanel } from '../components/ResizablePanel';
import { ProjectTables } from '../components/ProjectDetails/ProjectTables';
import { MiddlePane } from '../components/MiddlePane';
import { PANEL_CONSTANTS } from '../constants/panelConstants';

interface ProjectData {
  Lines: TreeNode;
}

const ProjectView: React.FC = () => {
  const [selectedNode, setSelectedNode] = useState<TreeNode | null>(null);
  const [selectedNodes, setSelectedNodes] = useState<string[]>([]);
  const [projectData, setProjectData] = useState<ProjectData | null>(null);
  const [leftPanelCollapsed, setLeftPanelCollapsed] = useState(false);
  const [rightPanelCollapsed, setRightPanelCollapsed] = useState(false);
  const theme = useTheme();
  const route = useRoute<any>();
  const projectId = route.params?.projectId;

  useEffect(() => {
    const fetchProjectData = async () => {
      if (!projectId) return;
      
      const db = getFirestore();
      const projectRef = doc(db, 'projects', projectId);
      const projectSnap = await getDoc(projectRef);
      
      if (projectSnap.exists()) {
        const data = projectSnap.data();
        console.log('Project data structure:', {
          rawData: data.data,  // Show the raw data
          propertyNames: {     // Compare property names
            inData: Object.keys(data.data || {}),
            expectedInInterface: ['Lines', 'schemas', 'towerWeights']
          },
          towerWeightsStructure: data.data?.towerWeights ? {
            hasWeightInfo: !!data.data.towerWeights.WeightInfo,
            firstWeight: data.data.towerWeights.WeightInfo?.[0],
            propertyNames: Object.keys(data.data.towerWeights.WeightInfo?.[0] || {})
          } : null
        });

        console.log('Project data structure:', {
          towerWeights: data.data?.towerWeights,  // Show the actual towerWeights object
          towerWeightsKeys: Object.keys(data.data?.towerWeights || {}),
          towerWeightsType: typeof data.data?.towerWeights,
          isArray: Array.isArray(data.data?.towerWeights)
        });

        if (data.data && data.data.Lines) {
          // Ensure towerWeights has the correct structure before setting
          if (data.data.towerWeights && !data.data.towerWeights.WeightInfo) {
            // If towerWeights is an array, wrap it in the correct structure
            if (Array.isArray(data.data.towerWeights)) {
              data.data.towerWeights = {
                WeightInfo: data.data.towerWeights
              };
            }
          }
          setProjectData(data.data);
        }
      }
    };

    fetchProjectData();
  }, [projectId]);

  const getAllChildrenNames = (node: TreeNode): string[] => {
    let names: string[] = [];
    if (node.children) {
      node.children.forEach(child => {
        names.push(child.name);
        names = names.concat(getAllChildrenNames(child));
      });
    }
    return names;
  };

  const handleNodeSelect = (node: TreeNode, selectChildren: boolean) => {
    setSelectedNode(node);
    
    let newSelection: string[] = [node.name];
    
    if (selectChildren && node.children) {
      const childNames = getAllChildrenNames(node);
      newSelection = [...newSelection, ...childNames];
    }
    
    setSelectedNodes(newSelection);
  };

  const renderTreeView = () => (
    <View style={styles.treeContainer}>
      {projectData?.Lines && (
        <TreeView
          data={projectData.Lines}
          onSelect={handleNodeSelect}
          selectedNodes={selectedNodes}
        />
      )}
    </View>
  );

  return (
    <View style={styles.container}>
      <AppHeader title="Project Structure" showBack />
      <View style={[styles.content, { backgroundColor: theme.colors.elevation.level1 }]}>
        <ResizablePanel
          position="left"
          defaultWidth={PANEL_CONSTANTS.LEFT_PANEL.DEFAULT_WIDTH}
          minWidth={PANEL_CONSTANTS.LEFT_PANEL.MIN_WIDTH}
          maxWidth={PANEL_CONSTANTS.LEFT_PANEL.MAX_WIDTH}
          onCollapse={setLeftPanelCollapsed}
        >
          <View style={[styles.treeContainer, { overflow: 'hidden' }]}>
            <ScrollView>
              {projectData?.Lines && (
                <TreeView
                  data={projectData.Lines}
                  onSelect={handleNodeSelect}
                  selectedNodes={selectedNodes}
                />
              )}
            </ScrollView>
          </View>
        </ResizablePanel>

        <View 
          style={[
            styles.mainContent,
            { 
              marginLeft: leftPanelCollapsed ? -20 : 0,
              marginRight: rightPanelCollapsed ? -20 : 0,
              backgroundColor: theme.colors.elevation.level1,
              width: leftPanelCollapsed || rightPanelCollapsed ? '110%' : '100%',
              transform: [
                {
                  translateX: leftPanelCollapsed ? 20 : 0
                }
              ]
            }
          ]}
        >
          <MiddlePane
            selectedNode={selectedNode}
            projectData={projectData}
          />
        </View>

        <ResizablePanel
          position="right"
          defaultWidth={PANEL_CONSTANTS.RIGHT_PANEL.DEFAULT_WIDTH}
          minWidth={PANEL_CONSTANTS.RIGHT_PANEL.MIN_WIDTH}
          maxWidth={PANEL_CONSTANTS.RIGHT_PANEL.MAX_WIDTH}
          onCollapse={setRightPanelCollapsed}
        >
          <ProjectTables
            selectedNode={selectedNode}
            projectData={projectData}
          />
        </ResizablePanel>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f0f0f0',
    minHeight: '100vh',
    width: '100%',
  },
  content: {
    flex: 1,
    flexDirection: 'row',
    overflow: 'hidden',
    height: 'calc(100vh - 64px)', // Subtract header height
  },
  treeContainer: {
    flex: 1,
    padding: 8,
    height: '100%',
    overflow: 'auto',
  },
  mainContent: {
    flex: 1,
    padding: '8px 16px',
    height: '100%',
    overflow: 'auto',
    minWidth: 0, // Important for flex child
  },
});

export default ProjectView;
